import { useId, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { csvToJson, jsonToCsv } from "../lib/csv";

const DEFAULT_CSV = "name,age\nAda,36\nGrace,45";
const DEFAULT_JSON = `[
  { "name": "Ada", "age": 36 },
  { "name": "Grace", "age": 45 }
]`;

type Mode = "c2j" | "j2c";

const taClass =
  "min-h-[220px] w-full rounded-md border border-border bg-card px-3 py-2 font-mono text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function CsvJsonTool() {
  const uid = useId();
  const [mode, setMode] = useState<Mode>("c2j");
  const [header, setHeader] = useState(true);
  const [csvText, setCsvText] = useState(DEFAULT_CSV);
  const [jsonText, setJsonText] = useState(DEFAULT_JSON);
  const [copied, setCopied] = useState(false);

  const result = useMemo(() => {
    if (mode === "c2j") {
      const r = csvToJson(csvText, { header });
      return r.ok
        ? { ok: true as const, text: JSON.stringify(r.data, null, 2) }
        : r;
    }
    const r = jsonToCsv(jsonText);
    return r.ok ? { ok: true as const, text: r.csv } : r;
  }, [mode, header, csvText, jsonText]);

  const outputText = result.ok ? result.text : "";

  function copy() {
    if (!result.ok || typeof navigator === "undefined") return;
    navigator.clipboard?.writeText(result.text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-pressed={mode === "c2j"}
          onClick={() => setMode("c2j")}
          className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
            mode === "c2j"
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border text-muted-foreground hover:text-foreground"
          }`}
        >
          {m.cj_mode_c2j()}
        </button>
        <button
          type="button"
          aria-pressed={mode === "j2c"}
          onClick={() => setMode("j2c")}
          className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
            mode === "j2c"
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border text-muted-foreground hover:text-foreground"
          }`}
        >
          {m.cj_mode_j2c()}
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-in`}>
            {mode === "c2j" ? m.cj_csv_input() : m.cj_json_input()}
          </Label>
          {mode === "c2j" ? (
            <textarea
              id={`${uid}-in`}
              className={taClass}
              value={csvText}
              spellCheck={false}
              onChange={(e) => setCsvText(e.target.value)}
            />
          ) : (
            <textarea
              id={`${uid}-in`}
              className={taClass}
              value={jsonText}
              spellCheck={false}
              onChange={(e) => setJsonText(e.target.value)}
            />
          )}
          {mode === "c2j" && (
            <label className="flex items-center gap-2 pt-1 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={header}
                onChange={(e) => setHeader(e.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
              {m.cj_header_label()}
            </label>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor={`${uid}-out`}>{m.cj_output_label()}</Label>
            <button
              type="button"
              onClick={copy}
              disabled={!result.ok}
              className="rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
            >
              {copied ? m.cj_copied() : m.cj_copy()}
            </button>
          </div>
          <textarea
            id={`${uid}-out`}
            data-testid="csv-output"
            readOnly
            className={taClass}
            value={outputText}
          />
          {!result.ok && (
            <p data-testid="csv-error" className="text-sm text-destructive">
              {result.error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
